// Project data for local development and production
export interface Project {
  id: number // Contract compliance: always number
  name: string
  description: string
  location: string
  status: 'active' | 'completed' | 'on-hold' | 'cancelled'
  clientName: string
  projectManager: string
  jobNumber: string
  startDate: string
  estimatedEndDate?: string
  createdAt: string
}

// Fake project data for development and production
export const MOCK_PROJECTS: Project[] = [
  {
    id: 1, // Contract-compliant numeric ID
    name: 'Environmental Assessment - Site A',
    description: 'Phase I Environmental Site Assessment for commercial development',
    location: 'Downtown Columbus, OH',
    status: 'active',
    clientName: 'Columbus Development Corp',
    projectManager: 'Sarah Johnson',
    jobNumber: '2401-1',
    startDate: '2024-01-15',
    estimatedEndDate: '2024-03-15',
    createdAt: '2024-01-10T10:00:00Z'
  },
  {
    id: 2, // Contract-compliant numeric ID
    name: 'Soil Remediation - Industrial Complex',
    description: 'Soil contamination remediation at former manufacturing facility',
    location: 'Industrial District, Cleveland, OH',
    status: 'active',
    clientName: 'Industrial Properties LLC',
    projectManager: 'Mike Chen',
    jobNumber: '2402-1',
    startDate: '2024-02-01',
    estimatedEndDate: '2024-06-30',
    createdAt: '2024-01-25T14:30:00Z'
  },
  {
    id: 3, // Contract-compliant numeric ID
    name: 'Groundwater Monitoring - Retail Center',
    description: 'Quarterly groundwater monitoring and reporting',
    location: 'Suburban Mall, Cincinnati, OH',
    status: 'active',
    clientName: 'Retail Properties Inc',
    projectManager: 'Lisa Rodriguez',
    jobNumber: '2401-2',
    startDate: '2024-01-01',
    estimatedEndDate: '2024-12-31',
    createdAt: '2023-12-15T09:15:00Z'
  },
  {
    id: 4, // Contract-compliant numeric ID
    name: 'Asbestos Abatement - Office Building',
    description: 'Asbestos inspection and abatement planning',
    location: 'Corporate Center, Toledo, OH',
    status: 'active',
    clientName: 'Corporate Real Estate Group',
    projectManager: 'David Kim',
    jobNumber: '2402-2',
    startDate: '2024-02-15',
    estimatedEndDate: '2024-04-15',
    createdAt: '2024-02-01T11:45:00Z'
  },
  {
    id: 5, // Contract-compliant numeric ID
    name: 'Tank Removal - Gas Station',
    description: 'Underground storage tank removal and site remediation',
    location: 'Highway 71, Akron, OH',
    status: 'active',
    clientName: 'Fuel Stop Enterprises',
    projectManager: 'Jennifer Walsh',
    jobNumber: '2403-1',
    startDate: '2024-03-01',
    estimatedEndDate: '2024-05-01',
    createdAt: '2024-02-20T16:20:00Z'
  },
  {
    id: 6, // Contract-compliant numeric ID
    name: 'Air Quality Assessment - Manufacturing Plant',
    description: 'Indoor air quality testing and ventilation assessment',
    location: 'Manufacturing District, Dayton, OH',
    status: 'active',
    clientName: 'Advanced Manufacturing Co',
    projectManager: 'Robert Taylor',
    jobNumber: '2402-3',
    startDate: '2024-02-20',
    estimatedEndDate: '2024-04-20',
    createdAt: '2024-02-10T13:10:00Z'
  },
  {
    id: 7, // Contract-compliant numeric ID
    name: 'Wetland Delineation - Residential Development',
    description: 'Wetland boundary survey and permitting support',
    location: 'Suburban Development, Youngstown, OH',
    status: 'active',
    clientName: 'Residential Builders LLC',
    projectManager: 'Amanda Foster',
    jobNumber: '2403-2',
    startDate: '2024-03-10',
    estimatedEndDate: '2024-05-10',
    createdAt: '2024-03-01T08:30:00Z'
  },
  {
    id: 8, // Contract-compliant numeric ID
    name: 'Lead Paint Assessment - School District',
    description: 'Lead-based paint inspection for school renovation',
    location: 'Elementary School, Canton, OH',
    status: 'completed',
    clientName: 'Canton City Schools',
    projectManager: 'Mark Stevens',
    jobNumber: '2311-1',
    startDate: '2023-11-01',
    estimatedEndDate: '2023-12-15',
    createdAt: '2023-10-20T12:00:00Z'
  },
  {
    id: 9, // Contract-compliant numeric ID
    name: 'Brownfield Assessment - Former Factory',
    description: 'Comprehensive environmental assessment for redevelopment',
    location: 'Industrial Zone, Lima, OH',
    status: 'on-hold',
    clientName: 'Urban Redevelopment Authority',
    projectManager: 'Patricia Moore',
    jobNumber: '2401-3',
    startDate: '2024-01-20',
    estimatedEndDate: '2024-08-20',
    createdAt: '2024-01-05T15:45:00Z'
  },
  {
    id: 10, // Contract-compliant numeric ID
    name: 'Waste Characterization - Chemical Plant',
    description: 'Hazardous waste identification and disposal planning',
    location: 'Chemical Complex, Findlay, OH',
    status: 'active',
    clientName: 'Chemical Industries Corp',
    projectManager: 'Thomas Wilson',
    jobNumber: '2402-4',
    startDate: '2024-02-25',
    estimatedEndDate: '2024-06-25',
    createdAt: '2024-02-15T10:20:00Z'
  },
  {
    id: 11, // Contract-compliant numeric ID
    name: 'Stormwater Management - Shopping Center',
    description: 'Stormwater system design and permitting',
    location: 'Shopping Plaza, Mansfield, OH',
    status: 'active',
    clientName: 'Commercial Properties Group',
    projectManager: 'Rachel Green',
    jobNumber: '2403-3',
    startDate: '2024-03-05',
    estimatedEndDate: '2024-07-05',
    createdAt: '2024-02-25T14:15:00Z'
  },
  {
    id: 12, // Contract-compliant numeric ID
    name: 'Mold Assessment - Office Complex',
    description: 'Mold inspection and remediation recommendations',
    location: 'Business Park, Springfield, OH',
    status: 'completed',
    clientName: 'Business Park Management',
    projectManager: 'Kevin Brown',
    jobNumber: '2312-1',
    startDate: '2023-12-01',
    estimatedEndDate: '2024-01-15',
    createdAt: '2023-11-20T09:30:00Z'
  },
  {
    id: 13, // Contract-compliant numeric ID
    name: 'Bruce Mansfield Power Plant - Shippingport',
    description: 'Environmental assessment and monitoring services for power plant facility',
    location: '128 Ferry Hill Road, Shippingport, PA 15077',
    status: 'active',
    clientName: 'Bruce Mansfield Power Plant c/o Zeigler Environmental',
    projectManager: 'John Meadows',
    jobNumber: '2407-1',
    startDate: '2024-03-15',
    estimatedEndDate: '2024-09-15',
    createdAt: '2024-03-10T08:00:00Z'
  }
]

// Helper functions for project data
export const getProjectById = (id: number): Project | undefined => {
  return MOCK_PROJECTS.find(project => project.id === id) // Exact numeric match
}

export const getProjectByName = (name: string): Project | undefined => {
  return MOCK_PROJECTS.find(project => project.name === name)
}

export const searchProjects = (query: string): Project[] => {
  if (!query || query.length < 2) {
    return MOCK_PROJECTS.filter(project => project.status === 'active').slice(0, 10)
  }
  
  const normalizedQuery = query.toLowerCase()
  return MOCK_PROJECTS
    .filter(project => 
      project.name.toLowerCase().includes(normalizedQuery) ||
      project.location.toLowerCase().includes(normalizedQuery) ||
      project.clientName.toLowerCase().includes(normalizedQuery) ||
      project.description.toLowerCase().includes(normalizedQuery)
    )
    .sort((a, b) => {
      // Sort by relevance: exact matches first, then name matches, then others
      const aName = a.name.toLowerCase()
      const bName = b.name.toLowerCase()
      
      if (aName.includes(normalizedQuery) && !bName.includes(normalizedQuery)) return -1
      if (!aName.includes(normalizedQuery) && bName.includes(normalizedQuery)) return 1
      
      return aName.localeCompare(bName)
    })
    .slice(0, 20)
}

export const getActiveProjects = (): Project[] => {
  return MOCK_PROJECTS.filter(project => project.status === 'active')
} 